import { ColorPicker } from '../../../../../primitives/color-picker';
import { ComboboxModel } from '../../../../combobox/combobox-model';
import { LabelWithoutRef } from '../../../../label/label-without-ref';
import { SelectModel } from '../../../../select/select-model';
import { InputNumberWithSteps } from './input-number-with-steps';

import { cn } from '../../../../../utils/cn';

import { EMAIL_COLORS } from '../../constants/colors';
import { FONT_FAMILY_MODEL, type FontFamily } from '../../constants/font-family';
import { HEADING_LEVEL_MODEL, type HeadingLevel } from '../../constants/font-level';
import { FONT_SIZE, FONT_SIZES } from '../../constants/font-sizes';
import { FONT_WEIGHT_MODEL, type FontWeight } from '../../constants/font-weight';

type TypographyProps = {
  backgroundColor?: string;
  className?: string;
  fontFamily?: FontFamily;
  fontLevel?: HeadingLevel;
  fontSize?: number;
  fontWeight?: FontWeight;
  isBackgroundColorVisible?: boolean;
  isFontFamilyVisible?: boolean;
  isFontLevelVisible?: boolean;
  isFontSizeVisible?: boolean;
  isFontWeightVisible?: boolean;
  isTextColorVisible?: boolean;
  textColor?: string;
  onBackgroundColorChange?: (backgroundColor?: string | null) => void;
  onFontFamilyChange?: (fontFamily: FontFamily) => void;
  onFontLevelChange?: (fontLevel: HeadingLevel) => void;
  onFontSizeChange?: (fontSize: number) => void;
  onFontWeightChange?: (fontWeight: FontWeight) => void;
  onTextColorChange?: (textColor?: string | null) => void;
};

export function Typography({
  backgroundColor,
  className,
  fontFamily,
  fontLevel,
  fontSize,
  fontWeight,
  isBackgroundColorVisible,
  isFontFamilyVisible,
  isFontLevelVisible,
  isFontSizeVisible,
  isFontWeightVisible,
  isTextColorVisible,
  textColor,
  onBackgroundColorChange,
  onFontFamilyChange,
  onFontLevelChange,
  onFontSizeChange,
  onFontWeightChange,
  onTextColorChange,
}: TypographyProps) {
  if (
    !isFontFamilyVisible &&
    !isFontWeightVisible &&
    !isFontSizeVisible &&
    !isFontLevelVisible &&
    !isTextColorVisible &&
    !isBackgroundColorVisible
  ) {
    return null;
  }

  return (
    <div className={cn('space-y-2', className)}>
      <p className="text-xs font-medium">Tipografía</p>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        {isFontFamilyVisible && (
          <div className="col-span-2">
            <LabelWithoutRef className="text-xs font-medium text-muted-foreground">
              Font family
            </LabelWithoutRef>
            <ComboboxModel
              model={FONT_FAMILY_MODEL}
              size="sm"
              value={fontFamily}
              variant="outline"
              onSelect={(value) => onFontFamilyChange?.(value as FontFamily)}
            />
          </div>
        )}
        {isFontSizeVisible && (
          <div>
            <LabelWithoutRef className="text-xs font-medium text-muted-foreground">
              Tamaño
            </LabelWithoutRef>
            <InputNumberWithSteps
              id="appearance-font-size"
              max={FONT_SIZE.max}
              min={FONT_SIZE.min}
              steps={FONT_SIZES}
              value={fontSize ?? null}
              onChange={(value) => {
                if (value !== null) {
                  onFontSizeChange?.(value);
                }
              }}
            />
          </div>
        )}
        {isFontLevelVisible && (
          <div>
            <LabelWithoutRef className="text-xs font-medium text-muted-foreground">
              Nivel
            </LabelWithoutRef>
            <SelectModel
              model={HEADING_LEVEL_MODEL}
              size="sm"
              value={fontLevel}
              variant="outline"
              onValueChange={(value) => onFontLevelChange?.(value as HeadingLevel)}
            />
          </div>
        )}
        {isFontWeightVisible && (
          <div>
            <LabelWithoutRef className="text-xs font-medium text-muted-foreground">
              Peso
            </LabelWithoutRef>
            <SelectModel
              model={FONT_WEIGHT_MODEL}
              size="sm"
              value={fontWeight}
              variant="outline"
              onValueChange={(value) => onFontWeightChange?.(value as FontWeight)}
            />
          </div>
        )}
        {isTextColorVisible && (
          <div>
            <LabelWithoutRef className="text-xs font-medium text-muted-foreground">
              Color
            </LabelWithoutRef>
            <ColorPicker
              colors={EMAIL_COLORS}
              isCleanColorVisible
              noneColorLabel="Sin personalizar"
              value={textColor}
              onValueChange={(value) => onTextColorChange?.(value ?? null)}
            />
          </div>
        )}
        {isBackgroundColorVisible && (
          <div>
            <LabelWithoutRef className="text-xs font-medium text-muted-foreground">
              Fondo
            </LabelWithoutRef>
            <ColorPicker
              colors={EMAIL_COLORS}
              isCleanColorVisible
              noneColorLabel="Sin personalizar"
              value={backgroundColor}
              onValueChange={(value) => onBackgroundColorChange?.(value ?? null)}
            />
          </div>
        )}
      </div>
    </div>
  );
}
